import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CounterCell } from "@/components/performance/CounterCell";
import { apiRequest } from "@/lib/queryClient";

export default function PerformanceCapturePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const { data: kid } = useQuery<{ firstName: string; lastName: string }>({
    queryKey: ["/api/players", id, "profile"],
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/players/${id}/sessions`);
      const json = await r.json();
      return json.data ?? json;
    },
    enabled: !!id,
  });

  const kidName = kid ? `${kid.firstName} ${kid.lastName}` : "Player";

  const [bowling, setBowling] = useState({
    ballsBowled: 0,
    bowlWideOutsideOff: 0, bowlOffStump: 0, bowlMiddleStump: 0,
    bowlLegStump: 0, bowlWideLeg: 0, bowlWides: 0, bowlNoBalls: 0,
    bowlFullToss: 0, bowlFull: 0, bowlGoodLength: 0,
    bowlShortOfLength: 0, bowlBouncer: 0, bowlYorker: 0,
  });

  const [batting, setBatting] = useState({
    batBallsFaced: 0, batCoverDrive: 0, batSquareCut: 0, batPullHook: 0,
    batFlickSweep: 0, batStraightDrive: 0, batDefended: 0,
    batFours: 0, batSixes: 0, batDotBalls: 0, batRunsScored: 0, batWickets: 0,
  });

  const [fielding, setFielding] = useState({
    fieldCatchesAttempted: 0, fieldCatchesTaken: 0,
    fieldGroundBalls: 0, fieldGroundClean: 0,
    fieldThrowsAttempted: 0, fieldThrowsOnTarget: 0,
    fieldRunOutsAttempted: 0, fieldRunOutsSuccess: 0,
  });

  const [fitness, setFitness] = useState({
    fitYoyoLevel: "", fitYoyoShuttles: "", fitSprint20mSec: "",
    fitShuttleRunSec: "", fitPushUps: "",
  });

  const bowlLineSum =
    bowling.bowlWideOutsideOff + bowling.bowlOffStump + bowling.bowlMiddleStump +
    bowling.bowlLegStump + bowling.bowlWideLeg + bowling.bowlWides + bowling.bowlNoBalls;
  const bowlLengthSum =
    bowling.bowlFullToss + bowling.bowlFull + bowling.bowlGoodLength +
    bowling.bowlShortOfLength + bowling.bowlBouncer + bowling.bowlYorker;

  const bowlSet = (key: keyof typeof bowling) => (v: number) =>
    setBowling((prev) => ({ ...prev, [key]: v }));
  const batSet = (key: keyof typeof batting) => (v: number) =>
    setBatting((prev) => ({ ...prev, [key]: v }));
  const fieldSet = (key: keyof typeof fielding) => (v: number) =>
    setFielding((prev) => ({ ...prev, [key]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: Record<string, number | undefined> = {
        kidId: Number(id),
        ...bowling,
        ...batting,
        ...fielding,
        ...(fitness.fitYoyoLevel ? { fitYoyoLevel: parseFloat(fitness.fitYoyoLevel) } : {}),
        ...(fitness.fitYoyoShuttles ? { fitYoyoShuttles: parseInt(fitness.fitYoyoShuttles) } : {}),
        ...(fitness.fitSprint20mSec ? { fitSprint20mSec: parseFloat(fitness.fitSprint20mSec) } : {}),
        ...(fitness.fitShuttleRunSec ? { fitShuttleRunSec: parseFloat(fitness.fitShuttleRunSec) } : {}),
        ...(fitness.fitPushUps ? { fitPushUps: parseInt(fitness.fitPushUps) } : {}),
      };
      await apiRequest("POST", "/api/performance-stats", body);
      navigate(-1);
    } finally {
      setSaving(false);
    }
  };

  const fieldRatio = (taken: number, attempted: number) =>
    attempted > 0 ? `${Math.round((taken / attempted) * 100)}%` : "—";

  return (
    <div className="flex flex-col min-h-screen bg-gray-50" data-testid="performance-capture-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <h1 className="text-sm font-semibold text-gray-900 truncate flex-1 text-center">
          {kidName} — Performance entry
        </h1>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="flex-1 px-4 py-4">
        <Tabs defaultValue="bowling">
          <TabsList className="w-full grid grid-cols-4 mb-4">
            <TabsTrigger value="bowling" data-testid="tab-bowling">Bowling</TabsTrigger>
            <TabsTrigger value="batting" data-testid="tab-batting">Batting</TabsTrigger>
            <TabsTrigger value="fielding" data-testid="tab-fielding">Fielding</TabsTrigger>
            <TabsTrigger value="fitness" data-testid="tab-fitness-capture">Fitness</TabsTrigger>
          </TabsList>

          {/* ── Bowling ── */}
          <TabsContent value="bowling" className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Balls bowled this drill</label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={0}
                  value={bowling.ballsBowled || ""}
                  onChange={(e) => setBowling((p) => ({ ...p, ballsBowled: parseInt(e.target.value) || 0 }))}
                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="0"
                />
                {bowling.ballsBowled > 0 && (
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${bowlLineSum > bowling.ballsBowled ? "bg-red-100 text-red-600" : "bg-green-100 text-green-700"}`}>
                    Line: {bowlLineSum} / {bowling.ballsBowled}
                  </span>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Line</p>
              <div className="grid grid-cols-3 gap-2">
                <CounterCell label="Wide off" value={bowling.bowlWideOutsideOff} onChange={bowlSet("bowlWideOutsideOff")} warn={bowlLineSum > bowling.ballsBowled} />
                <CounterCell label="Off stump" value={bowling.bowlOffStump} onChange={bowlSet("bowlOffStump")} />
                <CounterCell label="Middle" value={bowling.bowlMiddleStump} onChange={bowlSet("bowlMiddleStump")} />
                <CounterCell label="Leg stump" value={bowling.bowlLegStump} onChange={bowlSet("bowlLegStump")} />
                <CounterCell label="Wide leg" value={bowling.bowlWideLeg} onChange={bowlSet("bowlWideLeg")} />
                <CounterCell label="Wides" value={bowling.bowlWides} onChange={bowlSet("bowlWides")} />
                <CounterCell label="No balls" value={bowling.bowlNoBalls} onChange={bowlSet("bowlNoBalls")} />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Length</p>
              <div className="grid grid-cols-3 gap-2">
                <CounterCell label="Full toss" value={bowling.bowlFullToss} onChange={bowlSet("bowlFullToss")} />
                <CounterCell label="Full" value={bowling.bowlFull} onChange={bowlSet("bowlFull")} />
                <CounterCell label="Good length" value={bowling.bowlGoodLength} onChange={bowlSet("bowlGoodLength")} />
                <CounterCell label="Short" value={bowling.bowlShortOfLength} onChange={bowlSet("bowlShortOfLength")} />
                <CounterCell label="Bouncer" value={bowling.bowlBouncer} onChange={bowlSet("bowlBouncer")} />
                <CounterCell label="Yorker" value={bowling.bowlYorker} onChange={bowlSet("bowlYorker")} />
              </div>
              {bowling.ballsBowled > 0 && (
                <p className={`text-xs mt-2 text-right ${bowlLengthSum > bowling.ballsBowled ? "text-red-500" : "text-gray-500"}`}>
                  Length: {bowlLengthSum} / {bowling.ballsBowled}
                </p>
              )}
            </div>
          </TabsContent>

          {/* ── Batting ── */}
          <TabsContent value="batting" className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Balls faced</label>
              <input
                type="number"
                min={0}
                value={batting.batBallsFaced || ""}
                onChange={(e) => setBatting((p) => ({ ...p, batBallsFaced: parseInt(e.target.value) || 0 }))}
                className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="0"
              />
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Shot zones</p>
              <div className="grid grid-cols-2 gap-2">
                <CounterCell label="Cover drive" value={batting.batCoverDrive} onChange={batSet("batCoverDrive")} />
                <CounterCell label="Square cut" value={batting.batSquareCut} onChange={batSet("batSquareCut")} />
                <CounterCell label="Pull / hook" value={batting.batPullHook} onChange={batSet("batPullHook")} />
                <CounterCell label="Flick / sweep" value={batting.batFlickSweep} onChange={batSet("batFlickSweep")} />
                <CounterCell label="Straight drive" value={batting.batStraightDrive} onChange={batSet("batStraightDrive")} />
                <CounterCell label="Defended" value={batting.batDefended} onChange={batSet("batDefended")} />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Outcomes</p>
              <div className="grid grid-cols-4 gap-2">
                <CounterCell label="4s" value={batting.batFours} onChange={batSet("batFours")} />
                <CounterCell label="6s" value={batting.batSixes} onChange={batSet("batSixes")} />
                <CounterCell label="Dots" value={batting.batDotBalls} onChange={batSet("batDotBalls")} />
                <CounterCell label="Runs" value={batting.batRunsScored} onChange={batSet("batRunsScored")} />
              </div>
            </div>
          </TabsContent>

          {/* ── Fielding ── */}
          <TabsContent value="fielding" className="space-y-4">
            {[
              {
                label: "Catches",
                attempted: { key: "fieldCatchesAttempted" as const, display: "Attempted" },
                taken: { key: "fieldCatchesTaken" as const, display: "Taken" },
              },
              {
                label: "Ground balls",
                attempted: { key: "fieldGroundBalls" as const, display: "Attempted" },
                taken: { key: "fieldGroundClean" as const, display: "Clean" },
              },
              {
                label: "Throws",
                attempted: { key: "fieldThrowsAttempted" as const, display: "Attempted" },
                taken: { key: "fieldThrowsOnTarget" as const, display: "On target" },
              },
            ].map(({ label, attempted, taken }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-700">{label}</p>
                  <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                    {fieldRatio(fielding[taken.key], fielding[attempted.key])}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <CounterCell label={attempted.display} value={fielding[attempted.key]} onChange={fieldSet(attempted.key)} />
                  <CounterCell label={taken.display} value={fielding[taken.key]} onChange={fieldSet(taken.key)} warn={fielding[taken.key] > fielding[attempted.key]} />
                </div>
              </div>
            ))}
          </TabsContent>

          {/* ── Fitness ── */}
          <TabsContent value="fitness-capture" className="space-y-3">
            {[
              { key: "fitYoyoLevel" as const, label: "Yo-Yo level", step: "0.1", placeholder: "e.g. 14.2" },
              { key: "fitYoyoShuttles" as const, label: "Yo-Yo shuttles", step: "1", placeholder: "e.g. 28" },
              { key: "fitSprint20mSec" as const, label: "20m sprint (sec)", step: "0.01", placeholder: "e.g. 3.21" },
              { key: "fitShuttleRunSec" as const, label: "Shuttle run (sec)", step: "0.01", placeholder: "e.g. 11.4" },
              { key: "fitPushUps" as const, label: "Push-ups", step: "1", placeholder: "e.g. 30" },
            ].map(({ key, label, step, placeholder }) => (
              <div key={key} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4">
                <label className="text-sm font-medium text-gray-700 flex-1">{label}</label>
                <input
                  type="number"
                  min={0}
                  step={step}
                  value={fitness[key]}
                  onChange={(e) => setFitness((p) => ({ ...p, [key]: e.target.value }))}
                  className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm text-right"
                  placeholder={placeholder}
                />
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
